/**
 * Activity Cost Settings Page — `/polygon/settings/costs`
 *
 * Category filter pills, inline edit, override indicator, reset to default.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { FadeIn } from '../../components/animations/FadeIn';
import {
  ActivityCostItem,
  fetchActivityCosts,
  updateActivityCost,
  resetActivityCost,
} from '../../api/polygon';

const CATEGORIES = [
  { key: 'all', label: 'ALL' },
  { key: 'remediation', label: 'REMEDIATION' },
  { key: 'field_operations', label: 'FIELD OPS' },
  { key: 'well_installation', label: 'WELL INSTALL' },
  { key: 'sampling', label: 'SAMPLING' },
  { key: 'mobilization', label: 'MOBILIZATION' },
  { key: 'drilling', label: 'DRILLING' },
  { key: 'disposal', label: 'DISPOSAL' },
  { key: 'reporting', label: 'REPORTING' },
  { key: 'equipment', label: 'EQUIPMENT' },
  { key: 'escalation', label: 'ESCALATION' },
];

function formatCurrency(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${Math.round(value).toLocaleString()}`;
  return `$${value.toFixed(2)}`;
}

export function CostCatalogSettings() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<ActivityCostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  async function loadActivities() {
    try {
      setLoading(true);
      const resp = await fetchActivityCosts();
      setActivities(resp.activities);
    } catch (err) {
      console.error('Failed to load activity costs:', err);
      setError('Failed to load activity costs');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadActivities();
  }, []);

  const filtered = selectedCategory === 'all'
    ? activities
    : activities.filter(a => a.category === selectedCategory);

  async function handleSave(code: string) {
    const val = parseFloat(editValue);
    if (isNaN(val) || val <= 0) return;
    setSaving(true);
    try {
      await updateActivityCost(code, val);
      await loadActivities();
      setEditingCode(null);
    } catch (err) {
      console.error('Failed to update cost:', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleReset(code: string) {
    setSaving(true);
    try {
      await resetActivityCost(code);
      await loadActivities();
    } catch (err) {
      console.error('Failed to reset cost:', err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-mono-500 text-sm tracking-wider">LOADING COST CATALOG...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <p className="text-mono-500 text-sm mb-4">{error}</p>
        <button onClick={() => navigate(-1)} className="text-mono-400 text-xs hover:text-mono-200">BACK</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <FadeIn>
        <div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-mono-500 hover:text-mono-300 text-xs tracking-wider mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            BACK
          </button>
          <h1 className="text-2xl font-bold text-mono-100 tracking-wider font-orbitron">ACTIVITY COST CATALOG</h1>
          <p className="text-mono-500 text-sm mt-1">
            System defaults from BidEngine pricing data. Override per-client for custom pricing.
          </p>
        </div>
      </FadeIn>

      {/* Category Filter Pills */}
      <FadeIn delay={50}>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              onClick={() => setSelectedCategory(cat.key)}
              className={`px-3 py-1.5 rounded text-xs tracking-wider transition-colors ${
                selectedCategory === cat.key
                  ? 'bg-cyan-900/50 text-cyan-400 border border-cyan-700'
                  : 'bg-mono-800 text-mono-400 border border-mono-700 hover:text-mono-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </FadeIn>

      {/* Activity Cost Table */}
      <FadeIn delay={100}>
        <div className="bg-mono-900 border border-mono-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-mono-800">
                <th className="text-left text-mono-500 text-xs tracking-wider py-3 px-4">ACTIVITY</th>
                <th className="text-left text-mono-500 text-xs tracking-wider py-3 px-4">CATEGORY</th>
                <th className="text-left text-mono-500 text-xs tracking-wider py-3 px-4">UNIT</th>
                <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-4">COST</th>
                <th className="text-center text-mono-500 text-xs tracking-wider py-3 px-4">STATUS</th>
                <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-4">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(activity => (
                <tr key={activity.activity_code} className="border-b border-mono-800 hover:bg-mono-850">
                  <td className="py-3 px-4">
                    <div>
                      <span className="text-mono-200 text-sm">{activity.display_name}</span>
                      <span className="text-mono-600 text-xs block mt-0.5">{activity.activity_code}</span>
                      {activity.notes && (
                        <span className="text-mono-600 text-[10px] block mt-0.5 italic">{activity.notes}</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-mono-400 text-xs capitalize">{activity.category.replace(/_/g, ' ')}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-mono-400 text-xs">{activity.unit.replace(/_/g, ' ')}</span>
                  </td>
                  <td className="text-right py-3 px-4">
                    {editingCode === activity.activity_code ? (
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-mono-500 text-sm">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          className="w-28 bg-mono-800 border border-mono-600 rounded px-2 py-1 text-right text-mono-100 text-sm focus:outline-none focus:border-cyan-500"
                          autoFocus
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSave(activity.activity_code);
                            if (e.key === 'Escape') setEditingCode(null);
                          }}
                        />
                        <button
                          onClick={() => handleSave(activity.activity_code)}
                          disabled={saving}
                          className="p-1 text-cyan-400 hover:text-cyan-300 transition-colors"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingCode(null)}
                          className="p-1 text-mono-500 hover:text-mono-300 transition-colors"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className={`text-sm font-semibold ${
                          activity.is_override ? 'text-cyan-400' : 'text-mono-100'
                        }`}>
                          {activity.unit === 'percent' ? `${activity.default_cost}%` : formatCurrency(activity.default_cost)}
                        </span>
                        {activity.min_cost != null && activity.max_cost != null && (
                          <span className="text-mono-600 text-[10px] block">
                            range: {activity.unit === 'percent' ? `${activity.min_cost}–${activity.max_cost}%` : `${formatCurrency(activity.min_cost)}–${formatCurrency(activity.max_cost)}`}
                          </span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="text-center py-3 px-4">
                    {activity.is_override ? (
                      <span className="px-2 py-0.5 rounded text-[10px] tracking-wider bg-cyan-900/40 text-cyan-400 border border-cyan-800">
                        CUSTOM
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[10px] tracking-wider bg-mono-800 text-mono-500">
                        DEFAULT
                      </span>
                    )}
                  </td>
                  <td className="text-right py-3 px-4">
                    <div className="flex items-center justify-end gap-1">
                      {editingCode !== activity.activity_code && (
                        <button
                          onClick={() => {
                            setEditingCode(activity.activity_code);
                            setEditValue(activity.default_cost.toString());
                          }}
                          className="p-1.5 text-mono-500 hover:text-mono-200 transition-colors"
                          title="Edit cost"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                      {activity.is_override && (
                        <button
                          onClick={() => handleReset(activity.activity_code)}
                          disabled={saving}
                          className="p-1.5 text-mono-500 hover:text-mono-200 transition-colors"
                          title="Reset to default"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </FadeIn>

      {/* Info footer */}
      <FadeIn delay={150}>
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
          <p className="text-mono-600 text-xs">
            System defaults are calibrated from historical invoices and BidEngine bid data.
            Custom overrides apply to all cost projections for your organization.
            Changing a cost triggers automatic re-computation of projections for all active sites.
          </p>
        </div>
      </FadeIn>
    </div>
  );
}
